/**
 * Quest panel — shows the active mission + progress (toggle with Q),
 * and a reward toast when a mission completes.
 */

import { QUEST_CHAIN, getActiveQuest, type QuestDef, type QuestState } from "@craftgaming/shared";

export type QuestPanel = {
  refresh: (state: QuestState) => void;
  showCompletion: (quest: QuestDef) => void;
  dispose: () => void;
};

export function createQuestPanel(getState: () => QuestState): QuestPanel {
  const panel = document.createElement("div");
  panel.id = "quest-panel";
  panel.style.cssText = `
    position: absolute;
    top: 152px;
    right: 12px;
    width: 220px;
    padding: 10px 12px;
    background: rgba(0, 0, 0, 0.55);
    border: 1px solid rgba(255, 215, 0, 0.35);
    border-radius: 8px;
    color: #eee;
    font-size: 12px;
    line-height: 1.5;
    pointer-events: none;
  `;

  const overlay = document.getElementById("ui-overlay");
  (overlay ?? document.body).appendChild(panel);

  let visible = true;

  function refresh(state: QuestState) {
    const quest = getActiveQuest(state);
    if (!quest) {
      panel.innerHTML = `
        <div style="font-weight:700;color:#ffd700;margin-bottom:2px;">🏆 Missões completas!</div>
        <div style="color:#aaa;">Você terminou a jornada inicial.</div>
      `;
      return;
    }
    const done = state.progress;
    const total = quest.goal.count;
    const pct = Math.round((done / total) * 100);
    panel.innerHTML = `
      <div style="font-weight:700;color:#ffd700;margin-bottom:2px;">
        ⚑ Missão ${state.activeIndex + 1}/${QUEST_CHAIN.length}: ${quest.title}
      </div>
      <div>${quest.description}</div>
      <div style="margin-top:6px;background:#222;border-radius:4px;height:8px;overflow:hidden;">
        <div style="width:${pct}%;height:100%;background:#ffd700;transition:width 0.3s;"></div>
      </div>
      <div style="color:#aaa;margin-top:2px;">${done}/${total} · Recompensa: ${quest.rewardLabel}</div>
      <div style="color:#555;margin-top:4px;font-size:10px;">J mostra/esconde</div>
    `;
  }

  function showCompletion(quest: QuestDef) {
    const toast = document.createElement("div");
    toast.style.cssText = `
      position: fixed;
      top: 22%;
      left: 50%;
      transform: translateX(-50%);
      padding: 14px 26px;
      background: rgba(20, 20, 0, 0.85);
      border: 2px solid #ffd700;
      border-radius: 10px;
      color: #fff;
      text-align: center;
      z-index: 60;
      pointer-events: none;
      transition: opacity 0.8s;
    `;
    toast.innerHTML = `
      <div style="font-size:1.1rem;font-weight:800;color:#ffd700;">✔ Missão completa: ${quest.title}</div>
      <div style="font-size:0.85rem;margin-top:4px;">Recompensa recebida: ${quest.rewardLabel}</div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 900);
    }, 3500);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.code === "KeyJ") {
      // J = jornada/missões (Q is now "drop item", Minecraft-style)
      visible = !visible;
      panel.style.display = visible ? "block" : "none";
    }
  }
  document.addEventListener("keydown", handleKeyDown);

  refresh(getState());

  return {
    refresh,
    showCompletion,
    dispose() {
      document.removeEventListener("keydown", handleKeyDown);
      panel.remove();
    },
  };
}
